import { SimplePaletteColorOptions } from "@mui/material";
import { fontFaces } from "./fonts";
import createTheme from "@mui/material/styles/createTheme";

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    small: true;
  }
}

interface TextColors {
  primary: string;
  secondary: string | undefined;
  disabled: string;
  hint: string;
}

export const theme = (() => {
  const borderRadius = 4;

  const primaryColor: SimplePaletteColorOptions = {
    light: "rgba(149, 209, 255, 1)",
    main: "rgba(63, 171, 255, 1)",
    dark: "rgba(0, 46, 86, 1)",
    contrastText: "#fff",
  };

  const secondaryColor: SimplePaletteColorOptions = {
    light: "rgba(255, 195, 143, 1)",
    main: "rgba(245, 130, 32, 1)",
    dark: "rgba(216, 99, 1, 1)",
    contrastText: "#fff",
  };

  const successColor: SimplePaletteColorOptions = {
    light: "#c7fc55",
    main: "#93C918",
    dark: "#609800",
    contrastText: "#000",
  };

  const errorColor: SimplePaletteColorOptions = {
    light: "rgba(242, 138, 138, 1)",
    main: "rgba(255, 71, 71, 1)",
    dark: "rgba(148, 0, 0, 1)",
    contrastText: "#fff",
  };

  const warning = secondaryColor;

  const text: TextColors = {
    disabled: "rgba(0, 0, 0, 0.38)",
    hint: "rgba(0, 0, 0, 0.38)",
    primary: "rgba(0, 0, 0, 0.87)",
    secondary: "rgba(0, 0, 0, 0.54)",
  };

  const fallback =
    " -apple-system, BlinkMacSystemFont, avenir next, avenir, helvetica neue, helvetica, Ubuntu, roboto, noto, segoe ui, arial, sans-serif";
  const avenirFont = `Avenir, ${fallback}`;

  return createTheme({
    navigationDrawer: {
      width: 300,
    },
    caseDrawer: {
      width: 300,
    },
    palette: {
      primary: primaryColor,
      secondary: secondaryColor,
      text,
      success: successColor,
      warning,
      error: errorColor,
      background: {
        paper: "#FFF",
        default: "#F7F7F7",
      },
    },
    typography: {
      htmlFontSize: 16,
      // inspired by https://systemfontstack.com
      fontFamily: avenirFont,
      fontSize: 13,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 800,
      h1: {
        fontWeight: 600,
        fontSize: 20,
        letterSpacing: 0,
        color: "#000",
        lineHeight: 2,
      },
      h2: {
        fontWeight: 400,
        fontSize: 18,
        letterSpacing: 0,
        color: "#000",
        lineHeight: 1.8,
      },
      h3: {
        fontWeight: 600,
        fontSize: 16,
        letterSpacing: 0,
        color: "#000",
        lineHeight: 1.5,
      },
      h4: {
        fontWeight: 300,
        fontSize: 16,
        letterSpacing: 0,
        color: primaryColor.dark,
      },
      h5: {
        fontWeight: 400,
        fontSize: 14,
        letterSpacing: 0,
        color: "#000",
        fontStyle: "normal",
      },
      h6: {
        fontWeight: 300,
        fontSize: 14,
        letterSpacing: 0,
        color: "#000",
      },
      body1: {
        color: "#000",
        fontWeight: 400,
        fontSize: 14,
      },
      body2: {
        color: primaryColor.main,
        fontWeight: 400,
      },
    },
    shape: {
      borderRadius: borderRadius,
    },
    components: {
      MuiTypography: {
        variants: [
          {
            props: { variant: "small" },
            style: {
              fontSize: 13,
              color: "#9f9f9f",
            },
          },
        ],
      },
      MuiCssBaseline: {
        styleOverrides: {
          "@global": {
            "@font-face": fontFaces,
          },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: "#fff",
            color: "#3FABFF",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: Math.round(borderRadius / 6),
          },
          root: {},
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: Math.round(borderRadius / 6),
            marginBottom: "10px",
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: "#DDE6F0",
          },
        },
      },
      MuiButton: {
        variants: [
          {
            props: { variant: "contained", color: "warning" },
            style: {
              color: "#fff",
              backgroundColor: "#C65D32",
              backgroundImage: "linear-gradient(111deg, #C65D32 0%, #B94C3C 100%)",
              boxShadow: "0px 2px 4px #0000001A",
              "& .MuiButton-startIcon": {
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 16,
                height: 16,
                flexShrink: 0,
                marginLeft: 0,
                marginRight: 8,
                position: "relative",
                top: -1,
              },
              "& .MuiButton-startIcon .MuiSvgIcon-root": {
                display: "block",
                fontSize: 16,
              },
              "&:hover": {
                backgroundColor: "#B45129",
                backgroundImage: "linear-gradient(111deg, #B45129 0%, #A64234 100%)",
                boxShadow: "0px 3px 6px #00000024",
              },
              "&.Mui-disabled": {
                backgroundImage: "none",
                backgroundColor: "rgba(0, 0, 0, 0.12)",
                boxShadow: "none",
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            fontSize: "13px",
            borderRadius: "18px",
          },
          containedPrimary: {
            background: "transparent linear-gradient(111deg, #33A3DC 0%, #547CCC 100%) 0% 0% no-repeat padding-box",
            boxShadow: "0px 3px 6px #00000029",
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: "#F5F5F5",
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            backgroundColor: "transparent",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 800,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: "#FAFAFA",
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            backgroundColor: "#FFF",
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            overflow: "initial",
          },
        },
      },
    },
    mixins: {
      toolbar: {
        minHeight: 80,
      },
    },
    // overrides: {
  });
})();
